import { opportunityService } from '@/services/opportunity.service';
import axios from 'axios';

const initialState = () => ({
  tenderOpportunities: [],
  // Tender and id of the latest request; a response from any earlier request is dropped.
  tenderId: null,
  requestId: 0,
  status: {
    error: null,
    pending: false,
    resolved: false,
    rejected: false,
  },
});

const state = initialState();

// Never reset, so a late response cannot collide with a request made after a reset.
let nextRequestId = 1;

const actions = {
  getTenderOpportunities({ commit, state }, { tenderId }) {
    commit('GET_OPPORTUNITIES_BY_TENDER', { tenderId });
    const requestId = state.requestId;
    const pageSize = 99999;

    return opportunityService.getOpportunitiesByTenders({ tenderIds: [tenderId], pageSize }).then(
      (tenderOpportunities) => {
        commit('SUCCESS_GETTING_OPPORTUNITIES_BY_TENDER', {
          requestId,
          tenderId,
          tenderOpportunities: tenderOpportunities.items,
        });
        return tenderOpportunities.items;
      },
      (error) => {
        if (!axios.isCancel(error)) {
          commit('ERROR_GETTING_OPPORTUNITIES_BY_TENDER', { requestId, tenderId, error });
          throw error;
        }
      },
    );
  },
  async addOpportunity({ commit, dispatch }, newOpportunity) {
    commit('ADD_TENDER_OPPORTUNITY');
    return await dispatch('opportunities/addOpportunity', newOpportunity, { root: true })
      .then((response) => {
        commit('SUCCESS_ADDING_TENDER_OPPORTUNITY', response);
        return response;
      })
      .catch((error) => {
        commit('ERROR', error);
        throw error;
      });
  },
  RESET({ commit }) {
    commit('RESET_OPPORTUNITIES');
  },
};

const mutations = {
  GET_OPPORTUNITIES_BY_TENDER(state, { tenderId } = {}) {
    state.status = {
      ...state.status,
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
    state.tenderId = tenderId || null;
    state.requestId = nextRequestId++;
    state.tenderOpportunities = [];
  },
  SUCCESS_GETTING_OPPORTUNITIES_BY_TENDER(state, { requestId, tenderOpportunities }) {
    // Response of an earlier request: another tender, a superseded reload, or before a reset.
    if (requestId !== state.requestId) return;

    state.status = {
      ...state.status,
      resolved: true,
      pending: false,
    };
    state.tenderOpportunities = tenderOpportunities;
  },
  ERROR_GETTING_OPPORTUNITIES_BY_TENDER(state, { requestId, error }) {
    if (requestId !== state.requestId) return;

    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
    state.tenderOpportunities = [];
  },
  ADD_TENDER_OPPORTUNITY(state) {
    state.status = {
      ...state.status,
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  SUCCESS_ADDING_TENDER_OPPORTUNITY(state, newOpportunity) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    state.tenderOpportunities.push(newOpportunity);
  },
  SUCCESS_ADDING_DOCUMENTS(state, { opportunityId, documents }) {
    const opportunityIndex = state.tenderOpportunities.findIndex((opportunity) => opportunity.id === opportunityId);
    if (opportunityIndex < 0) return;
    const existing = state.tenderOpportunities[opportunityIndex].documents;
    const existingIds = new Set(existing.map((d) => d.id));
    const newDocs = documents.filter((d) => !existingIds.has(d.id));
    existing.push(...newDocs);
  },
  SUCCESS_DELETING_DOCUMENT(state, { opportunityId, documentId }) {
    const opportunityIndex = state.tenderOpportunities.findIndex((opportunity) => opportunity.id === opportunityId);
    if (opportunityIndex < 0) return;
    const documentIndex = state.tenderOpportunities[opportunityIndex].documents.findIndex(
      (doc) => doc.id === documentId,
    );

    if (documentIndex >= 0) state.tenderOpportunities[opportunityIndex].documents.splice(documentIndex, 1);
  },

  ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error: error,
    };
    state.tenderOpportunities = [];
  },
  RESET_OPPORTUNITIES(state) {
    state.tenderOpportunities = [];
    // Also invalidates responses still in flight.
    state.tenderId = null;
    state.requestId = 0;
  },
};

const getters = {
  tenderOpportunities: (state) => state.tenderOpportunities,

  // status
  error: (state) => state.status.error,
  pending: (state) => state.status.pending,
  rejected: (state) => state.status.rejected,
  resolved: (state) => state.status.resolved,
};

export const tenderOpportunities = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
